import { BarChart3, ArrowRight } from 'lucide-react';
import { ink, numEs } from '../../lib/utils';

/**
 * The four headline figures from Radiografía.
 *
 * They used to be four `p-4 rounded-2xl` boxes in a permanent two-column grid, each holding a
 * 2xl emoji above a 2xl number — a card the size of a postcard to deliver "9,4 %". And every one
 * of the four navigated to the same place, the top of /radiografia, leaving the reader to find
 * the figure they had just tapped.
 *
 * Now: one compact row, four across from sm, and each figure links to the section of Radiografía
 * that actually explains it — población to quiénes somos, paro to cómo trabajamos, renta
 * and vivienda to cómo vivimos. The anchor is honoured by Radiografía after its data loads, not
 * on mount, because on mount the page is still a spinner and the target does not exist yet.
 *
 * The paro figure (`cityStats.desempleo`, the API's key) is Radiografía's «Tasa de paro
 * registrado (estimación)»: registered unemployment over the population aged 16 to 64, not the
 * EPA unemployment rate. It was labelled «Desempleo»; its label says what it is, and wraps rather
 * than truncating to «Paro registrado (esti…».
 */
export function RadiografiaWidget({ cityStats, navigate }) {
  // No invented fallbacks: until the data arrives (or if a figure is missing) show a dash
  const n = (v) => (v != null && v !== '' && !Number.isNaN(Number(v)) ? Number(v) : null);
  const pob = n(cityStats?.poblacion), paro = n(cityStats?.desempleo), renta = n(cityStats?.renta), viv = n(cityStats?.viviendas);
  const stats = [
    { val: pob != null ? pob.toLocaleString('es-ES') : '—', label: 'Población', color: '#6366f1', section: 'quienes-somos' },
    { val: paro != null ? `${numEs(paro)} %` : '—', label: 'Paro registrado (estimación)', color: '#f59e0b', section: 'como-trabajamos' },
    { val: renta != null ? `${Math.round(renta / 1000)}k€` : '—', label: 'Renta/hogar', color: '#10b981', section: 'como-vivimos' },
    { val: viv != null ? viv.toLocaleString('es-ES') : '—', label: 'Viviendas', color: '#8b5cf6', section: 'como-vivimos' },
  ];

  return (
    <div data-testid="radiografia-widget">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-extrabold text-slate-700 uppercase tracking-wide flex items-center gap-1.5">
          <BarChart3 size={13} className="text-indigo-600" /> La ciudad en datos
        </h3>
        <button onClick={() => navigate('/radiografia')} className="text-[11px] text-torre-teal font-bold hover:underline flex items-center gap-0.5" data-testid="radiografia-see-all">
          Ver todo <ArrowRight size={11} />
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {stats.map((s, i) => (
          <button key={i} onClick={() => navigate(`/radiografia#${s.section}`)}
            className="px-2.5 py-2 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-100 transition-colors text-left"
            data-testid={`radiografia-stat-${s.section}`}>
            <p className="text-base sm:text-lg font-extrabold leading-none" style={{ color: ink(s.color) }}>{s.val}</p>
            <p className="text-[11px] text-slate-500 font-semibold mt-0.5 leading-tight">{s.label}</p>
          </button>
        ))}
      </div>
      <p className="text-[11px] text-slate-400 mt-2">INE · ICANE · SEPE</p>
    </div>
  );
}
